import React, { useMemo } from 'react';
import {
  WeekGrid, DayColumn, DayHeader, DayBody, RoomGroupLabel, MiniCard, EmptyState
} from './Timetable.styles';
import { isCancelled, formatTeachers } from '../../../api/timetableApi';

/**
 * Seven-day overview — the view closest to how the Google Sheet was read, so it is the
 * familiar landing point after the migration.
 *
 * Within a day, classes are grouped by room, because "what is running on Saturday" is
 * almost always asked room by room. Across campuses the grouping is by campus *and* room:
 * a room name only identifies a room within its own campus.
 */

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const formatDayDate = (date) =>
  date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });

const WeekView = ({ occurrences, weekStart, colorOf, onSelect, multiCampus = false }) => {
  const days = useMemo(() => {
    const result = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    for (let i = 0; i < 7; i += 1) {
      const date = new Date(weekStart);
      date.setDate(date.getDate() + i);
      const key = [
        date.getFullYear(),
        String(date.getMonth() + 1).padStart(2, '0'),
        String(date.getDate()).padStart(2, '0')
      ].join('-');

      result.push({
        key,
        label: DAY_LABELS[i],
        dateLabel: formatDayDate(date),
        isToday: date.getTime() === today.getTime(),
        groups: []
      });
    }

    const byDate = new Map(result.map(day => [day.key, day]));

    occurrences.forEach(occurrence => {
      const day = byDate.get(occurrence.date);
      if (!day) return;
      const room = occurrence.room || 'Unassigned';
      // Group on roomKey (already campus-qualified) so two campuses' "Room 3" stay apart,
      // and label with the campus so the reader can tell which is which.
      const key = multiCampus
        ? `${occurrence.campusId}::${room}`
        : room;
      const label = multiCampus && occurrence.campusName
        ? `${occurrence.campusName} · ${room}`
        : room;
      let group = day.groups.find(g => g.key === key);
      if (!group) {
        group = { key, label, unassigned: !occurrence.room, items: [] };
        day.groups.push(group);
      }
      group.items.push(occurrence);
    });

    result.forEach(day => {
      day.groups.sort((a, b) => {
        if (a.unassigned !== b.unassigned) return a.unassigned ? 1 : -1;
        return a.label.localeCompare(b.label, undefined, { numeric: true });
      });
      day.groups.forEach(group =>
        group.items.sort((a, b) => a.startTime.localeCompare(b.startTime))
      );
    });

    return result;
  }, [occurrences, weekStart, multiCampus]);

  const total = occurrences.length;

  return (
    <>
      <WeekGrid>
        {days.map(day => (
          <DayColumn key={day.key}>
            <DayHeader $today={day.isToday}>
              {day.label}
              <span>{day.dateLabel}</span>
            </DayHeader>
            <DayBody>
              {day.groups.map(group => (
                <React.Fragment key={group.key}>
                  <RoomGroupLabel>{group.label}</RoomGroupLabel>
                  {group.items.map(item => (
                    <MiniCard
                      key={item.id}
                      $color={colorOf(item)}
                      $cancelled={isCancelled(item)}
                      onClick={() => onSelect(item)}
                      title={item.title}
                    >
                      <strong>{item.title || '(untitled)'}</strong>
                      <span>{item.startTime}–{item.endTime}</span>
                      {formatTeachers(item) && <span> · {formatTeachers(item)}</span>}
                    </MiniCard>
                  ))}
                </React.Fragment>
              ))}
            </DayBody>
          </DayColumn>
        ))}
      </WeekGrid>

      {total === 0 && (
        <EmptyState>
          <h3>Nothing scheduled this week</h3>
          <p>No classes fall in this week for the selected campus and filters.</p>
        </EmptyState>
      )}
    </>
  );
};

export default WeekView;
